import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../features/AppState";
import {
  removeFetcherConfig,
  removeFetcherInterface,
  removeFetcherReport,
} from "../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";

export type CatalogueLevel = "config" | "report" | "interface";

interface Props {
  open: boolean;
  level: CatalogueLevel;
  itemId: string;
  onClose: () => void;
  /** opens the Configs tab with its report filter set to this report */
  onShowConfigs?: (reportId: string) => void;
}

/**
 * Confirmation before a delete, listing whatever still depends on the row.
 * A blocked delete is refused here for the same reason the endpoint refuses it
 * with a 409 - the dependants would be orphaned.
 */
const DeleteCatalogueItemModal = ({
  open,
  level,
  itemId,
  onClose,
  onShowConfigs,
}: Props) => {
  const dispatch = useDispatch();
  const { configs, reports, interfaces } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  let title = "";
  let subtitle = "";
  let blockers: string[] = [];
  let note = "";
  let reportRows: typeof reports = [];

  if (level === "config") {
    const config = configs.find((row) => row.id === itemId);
    if (config) {
      title = `Delete ${config.name}? (${config.id})`;
      subtitle = `${interfaces.find((row) => row.id === config.iface)?.name ?? "-"} · ${config.report_name}`;
      if (config.used > 0) {
        blockers.push(
          `${config.used} brand${config.used > 1 ? "s are" : " is"} connected. Each owns a raw_data_<link_id> table that would be orphaned.`,
        );
      }
      note = "Disconnect the brands first on Manual Configuration.";
    }
  }

  if (level === "report") {
    const report = reports.find((row) => row.id === itemId);
    if (report) {
      title = `Delete ${report.name}? (${report.id})`;
      subtitle = interfaces.find((row) => row.id === report.iface)?.name ?? "-";
      if (report.configs > 0) {
        blockers.push(
          `${report.configs} config${report.configs > 1 ? "s reference" : " references"} this report.`,
        );
      }
      if (report.objects > 0) {
        blockers.push(
          `${report.objects} object${report.objects > 1 ? "s are" : " is"} attached and would go with it.`,
        );
      }
      note = "Delete or repoint the configs first.";
    }
  }

  if (level === "interface") {
    const backendInterface = interfaces.find((row) => row.id === itemId);
    if (backendInterface) {
      title = `Delete ${backendInterface.name}? (${backendInterface.id})`;
      subtitle = "Backend interface";
      if (backendInterface.brands > 0) {
        blockers.push(
          `${backendInterface.brands} brand account${backendInterface.brands > 1 ? "s are" : " is"} bound to it.`,
        );
      }
      reportRows = reports.filter((row) => row.iface === backendInterface.id);
      if (reportRows.length) {
        blockers.push(
          `${reportRows.length} report${reportRows.length > 1 ? "s belong" : " belongs"} to it.`,
        );
      }
      note =
        backendInterface.brands > 0
          ? "An interface with brand accounts bound to it can never be deleted."
          : "Delete every report first.";
    }
  }

  const isBlocked = blockers.length > 0;

  const handleDelete = () => {
    // TODO: DELETE /aladdin_api/v1/data_collection/fetcher_<level>/<shared_uid>
    if (level === "config") dispatch(removeFetcherConfig(itemId));
    if (level === "report") dispatch(removeFetcherReport(itemId));
    if (level === "interface") dispatch(removeFetcherInterface(itemId));
    dispatch(
      setDisplayErrorSnackBar({
        message: "Deleted. The row is gone.",
        type: "success",
      }),
    );
    onClose();
  };

  const handleDeleteReport = (reportId: string) => {
    // a report with configs pointing at it would be rejected with a 409
    const report = reports.find((row) => row.id === reportId);
    if (!report || report.configs > 0) return;
    dispatch(removeFetcherReport(reportId));
    dispatch(
      setDisplayErrorSnackBar({
        message: `${report.name} removed with ${report.objects} object${report.objects === 1 ? "" : "s"}.`,
        type: "success",
      }),
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "560px", maxWidth: "94vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>{title}</DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
        {subtitle}
      </Typography>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        {isBlocked ? (
          <>
            <Alert severity="error">
              <b>Cannot delete.</b> {blockers.join(" ")}
            </Alert>
            <Typography sx={{ fontSize: "12px", color: "#00000099", mt: 1.5 }}>
              {note}
            </Typography>
          </>
        ) : (
          <Alert severity="warning">
            Nothing depends on this row. Deletion is permanent.
          </Alert>
        )}

        {level === "interface" && reportRows.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "#00000099",
                fontWeight: 700,
                mb: 1,
              }}
            >
              Reports on this interface · {reportRows.length}
            </Typography>
            <Stack spacing={1}>
              {reportRows.map((report) => {
                const reportBlocked = report.configs > 0;
                return (
                  <Stack
                    key={report.id}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      border: "1px solid #0000001F",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>
                        {report.name} ({report.id})
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: reportBlocked ? "#C0392B" : "#00000099",
                        }}
                      >
                        {reportBlocked
                          ? `${report.configs} config${report.configs > 1 ? "s" : ""} reference this report - delete them first`
                          : `${report.objects} object${report.objects === 1 ? "" : "s"} would go with it`}
                      </Typography>
                    </Box>
                    {reportBlocked ? (
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() => {
                          onShowConfigs?.(report.id);
                          onClose();
                        }}
                      >
                        See configs
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteReport(report.id)}
                      >
                        Delete report
                      </Button>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={isBlocked}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCatalogueItemModal;
